import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import TrustFilieBadge from '../shared/TrustFilieBadge';
import { type FieldSource } from '../shared/TrustFilieBadge';
import {
  updateFilingStatus,
  updatePersonalInfo,
  updateAnswers,
  markFieldOverride,
} from '../../store/questionnaireSlice';

const filingStatusOptions = [
  { value: 'single', label: 'Single' },
  { value: 'married-jointly', label: 'Married Filing Jointly' },
  { value: 'married-separately', label: 'Married Filing Separately' },
  { value: 'head-of-household', label: 'Head of Household' },
  { value: 'qualifying-widow', label: 'Qualifying Widow(er)' },
];

const currentYear = new Date().getFullYear();
const taxYearOptions = [currentYear - 1, currentYear - 2, currentYear];

export default function DetectedInfoStep() {
  const dispatch = useDispatch();
  const { answers, smartDefaults, fieldOverrides } = useSelector(
    (state: RootState) => state.questionnaire
  );

  const hasSmartDefaults =
    smartDefaults !== null && smartDefaults.documentsStatus.analyzed > 0;

  // Local form state seeded from answers or smart defaults
  const [taxYear, setTaxYear] = useState<number>(
    smartDefaults?.detectedTaxYear || currentYear - 1
  );
  const [name, setName] = useState(
    answers.personalInfo?.name || smartDefaults?.primaryFiler?.name || ''
  );
  const [stateOfResidence, setStateOfResidence] = useState(
    answers.personalInfo?.stateOfResidence ||
      smartDefaults?.primaryFiler?.state ||
      ''
  );
  const [ssn, setSsn] = useState(answers.personalInfo?.ssn || '');
  const [dob, setDob] = useState(answers.personalInfo?.dateOfBirth || '');
  const [occupation, setOccupation] = useState(
    answers.personalInfo?.occupation || ''
  );

  const [spouseName, setSpouseName] = useState(
    answers.personalInfo?.spouseInfo?.name ||
      smartDefaults?.spouseDetected?.name ||
      ''
  );
  const [spouseSsn, setSpouseSsn] = useState(
    answers.personalInfo?.spouseInfo?.ssn || ''
  );
  const [spouseDob, setSpouseDob] = useState(
    answers.personalInfo?.spouseInfo?.dateOfBirth || ''
  );
  const [spouseOccupation, setSpouseOccupation] = useState(
    answers.personalInfo?.spouseInfo?.occupation || ''
  );

  const filingStatus =
    answers.filingStatus || smartDefaults?.suggestedFilingStatus || '';

  // Apply smart defaults on mount if not already overridden
  useEffect(() => {
    if (!hasSmartDefaults) return;
    if (!fieldOverrides['filingStatus'] && smartDefaults?.suggestedFilingStatus && !answers.filingStatus) {
      dispatch(updateFilingStatus(smartDefaults.suggestedFilingStatus));
    }
  }, [hasSmartDefaults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync personal info to Redux on blur
  const syncPersonalInfo = () => {
    const base = {
      name,
      ssn,
      dateOfBirth: dob,
      occupation,
      stateOfResidence,
    };

    if (filingStatus === 'married-jointly') {
      dispatch(
        updateAnswers({
          personalInfo: {
            ...base,
            spouseInfo: {
              name: spouseName,
              ssn: spouseSsn,
              dateOfBirth: spouseDob,
              occupation: spouseOccupation,
            },
          },
        })
      );
    } else {
      dispatch(updatePersonalInfo(base));
    }
  };

  const handleFilingStatusChange = (value: string) => {
    dispatch(updateFilingStatus(value));
    dispatch(markFieldOverride('filingStatus'));
  };

  const isMFJ = filingStatus === 'married-jointly';

  const nameSource: FieldSource =
    fieldOverrides['name']
      ? 'user'
      : smartDefaults?.primaryFiler?.name
        ? 'document'
        : 'user';

  const stateSource: FieldSource =
    fieldOverrides['stateOfResidence']
      ? 'user'
      : smartDefaults?.primaryFiler?.state
        ? 'document'
        : 'user';

  const filingStatusSource: FieldSource =
    fieldOverrides['filingStatus']
      ? 'user'
      : smartDefaults?.suggestedFilingStatus
        ? 'document'
        : 'user';

  const spouseNameSource: FieldSource =
    fieldOverrides['spouseName']
      ? 'user'
      : smartDefaults?.spouseDetected?.name
        ? 'document'
        : 'user';

  const inputClasses =
    'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Here's what we found
        </h2>
        <p className="text-gray-500 mt-1">
          Review and confirm the information detected from your documents
        </p>
      </div>

      <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
        <p className="text-sm text-blue-900 font-medium">
          Filie currently covers federal return analysis only.
        </p>
        <p className="text-sm text-blue-800 mt-1">
          We still want to know your state of residence so we can learn which state-return needs matter most in beta, but state return preparation is not included yet.
        </p>
      </div>

      {/* No smart defaults banner */}
      {!hasSmartDefaults && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            Upload and analyze your documents for smart pre-fill. You can still
            fill in the form manually below.
          </p>
        </div>
      )}

      {/* Tax Year */}
      <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Tax Year</h3>
          {smartDefaults?.detectedTaxYear && (
            <TrustFilieBadge source="document" />
          )}
        </div>
        <select
          value={taxYear}
          onChange={(e) => setTaxYear(Number(e.target.value))}
          className={inputClasses}
        >
          {taxYearOptions.map((yr) => (
            <option key={yr} value={yr}>
              {yr}
            </option>
          ))}
        </select>
        {smartDefaults?.taxYearWarning && (
          <p className="text-sm text-amber-600 mt-2">
            {smartDefaults.taxYearWarning}
          </p>
        )}
      </div>

      {/* Filing Status */}
      <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-gray-900">Filing Status</h3>
          <TrustFilieBadge source={filingStatusSource} />
        </div>
        {smartDefaults?.filingStatusReason && (
          <p className="text-sm text-gray-500 mb-3">
            {smartDefaults.filingStatusReason}
          </p>
        )}
        <div className="space-y-2">
          {filingStatusOptions.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-white cursor-pointer transition-colors"
            >
              <input
                type="radio"
                name="filingStatus"
                value={opt.value}
                checked={filingStatus === opt.value}
                onChange={() => handleFilingStatusChange(opt.value)}
                className="w-4 h-4 text-gray-600 cursor-pointer"
              />
              <span className="ml-3 text-sm text-gray-900">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Primary Filer */}
      <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
        <h3 className="font-semibold text-gray-900 mb-4">
          Primary Filer
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              {name && <TrustFilieBadge source={nameSource} />}
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                dispatch(markFieldOverride('name'));
              }}
              onBlur={syncPersonalInfo}
              placeholder="First Last"
              className={inputClasses}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                State of Residence
              </label>
              {stateOfResidence && <TrustFilieBadge source={stateSource} />}
            </div>
            <input
              type="text"
              value={stateOfResidence}
              onChange={(e) => {
                setStateOfResidence(e.target.value);
                dispatch(markFieldOverride('stateOfResidence'));
              }}
              onBlur={syncPersonalInfo}
              placeholder="e.g., New York"
              className={inputClasses}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SSN
            </label>
            <input
              type="text"
              value={ssn}
              onChange={(e) => setSsn(e.target.value)}
              onBlur={syncPersonalInfo}
              placeholder="XXX-XX-XXXX"
              className={inputClasses}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              onBlur={syncPersonalInfo}
              className={inputClasses}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Occupation
            </label>
            <input
              type="text"
              value={occupation}
              onChange={(e) => setOccupation(e.target.value)}
              onBlur={syncPersonalInfo}
              placeholder="e.g., Software Engineer"
              className={inputClasses}
            />
          </div>
        </div>
      </div>

      {/* Spouse Section - shown only if MFJ */}
      {isMFJ && (
        <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Spouse Information</h3>
            {spouseName && <TrustFilieBadge source={spouseNameSource} />}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Spouse Full Name
              </label>
              <input
                type="text"
                value={spouseName}
                onChange={(e) => {
                  setSpouseName(e.target.value);
                  dispatch(markFieldOverride('spouseName'));
                }}
                onBlur={syncPersonalInfo}
                placeholder="First Last"
                className={inputClasses}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Spouse SSN
              </label>
              <input
                type="text"
                value={spouseSsn}
                onChange={(e) => setSpouseSsn(e.target.value)}
                onBlur={syncPersonalInfo}
                placeholder="XXX-XX-XXXX"
                className={inputClasses}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Spouse Date of Birth
              </label>
              <input
                type="date"
                value={spouseDob}
                onChange={(e) => setSpouseDob(e.target.value)}
                onBlur={syncPersonalInfo}
                className={inputClasses}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Spouse Occupation
              </label>
              <input
                type="text"
                value={spouseOccupation}
                onChange={(e) => setSpouseOccupation(e.target.value)}
                onBlur={syncPersonalInfo}
                placeholder="e.g., Teacher"
                className={inputClasses}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
